import {Dispatcher, EventBus, EventBusRegistry} from 'dispatcher'
import {ChatService} from '../services/ChatService'
import {Subscription} from 'observables'
import {ChatAdapterDispatcher} from './ChatAdapterDispatcher'
import {ChatMessage} from '../services/ChatMessageSet'
import {SendMessageData} from '../services/types'
import {TimelineEventProducer} from '../../TimelineEventProducer'
import {TimelineAdapter, TimelineEventData} from '../../TimelineAdapter'
import {
  WS_ASSISTANT_METADATA,
  WS_DIRECT_MESSAGE_RECEIVE_DATA
} from '../../../ws'

export interface ChatMessagesProps {
  userId: string
  message: string
  date: number
  sent?: boolean
  eventId: string
  producer: TimelineEventProducer
  metadata?: WS_ASSISTANT_METADATA
}

export class ChatAdapter extends TimelineAdapter<ChatMessagesProps> {
  static id = 'message'
  protected dispatcher!: EventBusRegistry
  protected readonly reconnectSubscription: Subscription
  public readonly service: ChatService = ChatService.shared(this.eventBus)
  protected shouldSync = false

  constructor(
    // This is the react component that will be rendered in timeline
    // eslint-disable-next-line @typescript-eslint/ban-types
    protected element: Function,
    protected eventBus: EventBus,
    public producer: TimelineEventProducer,
    protected assignmentId: string,
    protected scope: 'CUSTOMER' | 'CONTRACTOR',
    protected avatar?: string,
    protected logo?: string
  ) {
    super()
    this.configureDispatcher()
    this.reconnectSubscription = this.eventBus.connection.subscribe(
      (connected) => {
        if (connected) {
          /**
           * When connecting for first time there is no need to sync the events,
           * but if we reconnect to WS(ws was closed for some reason) we should
           * try to fetch the new events that might be occurred during the closed ws window
           */
          if (this.shouldSync) this.syncMissing().then()
          else this.shouldSync = true
        }
      }
    )
  }

  protected createDispatcher(): Dispatcher<
    WS_DIRECT_MESSAGE_RECEIVE_DATA,
    unknown
  > {
    return ChatAdapterDispatcher.shared(this.eventBus, this.assignmentId)
  }

  protected configureDispatcher() {
    // PUSH EVENT IN TIMELINE FOR WHEN USER IS ON TIMELINE
    this.dispatcher = this.createDispatcher().register((event) => {
      const message = this.service.DDMTOChatMessage(event.data!)
      this.producer.update([this.chatEventToTimelineEvent(message)])
      // Mark messages as seen on received
      if (
        message.senderUserId !== this.eventBus.credentials?.userId ||
        message.senderUserId === message.recipientUserId
      ) {
        this.service.markMessageAsSeen(message.chatId, message.id)
        // Enabled chat if it is disabled
        this.enableChat()
      }
    })
  }

  protected async syncMissing() {
    const sync = await this.load()
    const missed = sync.filter(
      (event) =>
        !this.producer
          .getHistory()
          .map((tmEvent) => tmEvent._id)
          .includes(event._id)
    )
    this.producer.update(missed)
  }

  // INITIAL LOADING ALL CONVERSATIONS
  async load(): Promise<TimelineEventData<ChatMessagesProps>[]> {
    const history = await this.service.loadHistory(this.assignmentId)
    if (history.length > 0) {
      const lastMessage = history.slice(-1)[0]
      this.service.markMessageAsSeenIfConversationsHasUnseen(
        lastMessage.chatId,
        lastMessage.id
      )
    }
    return history.map(this.chatEventToTimelineEvent.bind(this))
  }

  protected chatEventToTimelineEvent(
    event: ChatMessage
  ): TimelineEventData<ChatMessagesProps> {
    return {
      _id: event.id,
      date: event.date,
      alignment:
        event.senderUserId !== this.eventBus.credentials?.userId
          ? 'flex-start'
          : 'flex-end',
      avatar: this.getMessageAvatar(event),
      element: this.element,
      props: {
        eventId: event.id,
        userId: event.senderUserId,
        message: event.message,
        date: event.date,
        sent: event.sent,
        producer: this.producer,
        metadata: event.metadata
      },
      producer: this.producer,
      adapterId: ChatAdapter.id
    }
  }

  protected enableChat() {
    if (this.producer.actionManager.isChatDisabled()) {
      this.producer.actionManager.enableChat()
    }
  }

  getMessageAvatar(event: ChatMessage) {
    if (this.scope === 'CUSTOMER') {
      return event.senderUserId === this.eventBus.credentials?.userId
        ? this.avatar
        : this.logo
    }
    return event.senderUserId === this.eventBus.credentials?.userId
      ? this.logo
      : this.avatar
  }

  async sendMessage(
    message: SendMessageData
  ): Promise<TimelineEventData<ChatMessagesProps>> {
    const event = await this.service.sendMessage(message)
    return this.chatEventToTimelineEvent(event)
  }

  complete() {
    this.reconnectSubscription.unsubscribe()
    ChatAdapterDispatcher.shared(this.eventBus).clearAssignmentId()
    this.dispatcher.unregister()
  }
}
